import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { useT } from "@/lib/i18n";
import tracking from "@/lib/tracking";
import { quizHref, StickyCta } from "../templates/shared";

/**
 * "Official document" landing layout (Office of Gay Friendship Compliance).
 * The page is the rest of the absurdist document the ad started; the single
 * CTA is the signature line.
 *
 * Two ways to use it:
 * - Built-in men's doc pages (/f4cd /f4js /f4ob) resolve copy from i18n
 *   (pages.docs) via the exported DocCeaseDesist/DocJurySummons/DocObituary.
 * - As the "document" template for MCP-created landing variants: DocLayout
 *   takes a fully-resolved DocConfig. See docs/landing-templates.md.
 *
 * The paper sheet is deliberately light in both themes (paper is paper);
 * the backdrop follows the app theme. Body font is Georgia: the brand serif
 * (Fraunces) renders slanted and hurts long-form readability.
 */

export interface DocConfig {
  posthogEvent: string;
  audienceParam: string;
  eventTypeParam?: string;
  letterhead: string;
  docket: string;
  stamp: string;
  title: string;
  re: string;
  intros: string[];
  findingsTitle: string;
  exhibits: string[];
  remedyTitle: string;
  remedy: string;
  signLine: string;
  cta: string;
  ctaSub: string;
  witnessTitle: string;
  witnesses: Array<{ quote: string; author: string }>;
  objectionsTitle: string;
  objections: Array<{ q: string; a: string }>;
  footerGag: string;
  /** i18n key for the owned-label line; defaults to gay-owned. */
  ownedLabel?: "gay" | "lgbtq";
}

function SectionRule({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 mt-8 mb-3">
      <span className="text-xs font-bold tracking-[0.2em] uppercase text-neutral-500">
        {label}
      </span>
      <span className="flex-1 border-t border-neutral-300" />
    </div>
  );
}

export function DocLayout({ config }: { config: DocConfig }) {
  const { t: tRaw } = useT("landing");

  useEffect(() => {
    tracking.trackPage(config.posthogEvent);
  }, [config.posthogEvent]);

  const ctaHref = quizHref(config.audienceParam, config.eventTypeParam);

  return (
    <div className="min-h-full bg-neutral-200 dark:bg-neutral-900 py-6 sm:py-12 px-3 sm:px-6 pb-28">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative max-w-2xl mx-auto bg-[#fdfbf4] text-neutral-900 shadow-2xl rounded-sm px-6 sm:px-12 py-10"
        style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
      >
        {/* stamp */}
        <div className="absolute top-6 right-4 sm:right-8 rotate-[8deg] border-[3px] border-red-600/70 text-red-600/80 rounded px-3 py-1 text-xs sm:text-sm font-bold tracking-[0.25em] uppercase font-sans">
          {config.stamp}
        </div>

        {/* letterhead */}
        <div className="text-center border-b-2 border-neutral-800 pb-4">
          <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 font-sans">
            {config.letterhead}
          </p>
          <h1 className="text-3xl sm:text-4xl font-bold mt-2 tracking-tight font-serif">
            {config.title}
          </h1>
          <p className="text-xs font-mono text-neutral-500 mt-2">{config.docket}</p>
        </div>

        <p className="mt-6 font-semibold italic">{config.re}</p>
        {config.intros.map((p, i) => (
          <p key={i} className="mt-4 leading-relaxed">
            {p}
          </p>
        ))}

        <SectionRule label={config.findingsTitle} />
        <ol className="flex flex-col gap-3">
          {config.exhibits.map((ex, i) => (
            <li key={i} className="flex gap-4">
              <span className="text-[11px] font-bold tracking-widest uppercase text-neutral-400 font-sans pt-1 shrink-0 w-16">
                {tRaw("pages.docs.exhibitLabel", {
                  letter: String.fromCharCode(65 + i),
                })}
              </span>
              <span className="leading-relaxed border-l-2 border-neutral-300 pl-4">{ex}</span>
            </li>
          ))}
        </ol>

        <SectionRule label={config.remedyTitle} />
        <p className="leading-relaxed">{config.remedy}</p>

        {/* signature CTA */}
        <div className="mt-10 border-2 border-dashed border-neutral-400 rounded-lg p-5 text-center bg-white/60">
          <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-sans mb-4">
            {config.signLine}
          </p>
          <a
            href={ctaHref}
            className="inline-flex items-center justify-center rounded-full bg-primary-500 hover:bg-primary-600 text-white font-bold text-lg px-12 py-4 shadow-lg shadow-primary-500/30 transition-colors font-sans w-full sm:w-auto"
          >
            {config.cta}
          </a>
          <p className="text-sm text-neutral-500 mt-3 font-sans">{config.ctaSub}</p>
        </div>

        <SectionRule label={config.witnessTitle} />
        <div className="flex flex-col gap-4">
          {config.witnesses.map((w, i) => (
            <div key={i} className="border-l-2 border-neutral-300 pl-4">
              <p className="italic leading-relaxed">“{w.quote}”</p>
              <p className="text-sm text-neutral-500 mt-1 font-sans">
                {tRaw("pages.docs.swornBy", { author: w.author })}
              </p>
            </div>
          ))}
        </div>

        <SectionRule label={config.objectionsTitle} />
        <div className="flex flex-col gap-4">
          {config.objections.map((o, i) => (
            <div key={i}>
              <p className="font-semibold">{o.q}</p>
              <p className="leading-relaxed text-neutral-700 mt-1">{o.a}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-4 border-t border-neutral-300 text-center text-xs text-neutral-500 font-sans">
          <p className="mb-2">{config.footerGag}</p>
          <p className="flex items-center justify-center gap-1.5 mb-2">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-red-500 via-yellow-400 to-violet-500" />
            {tRaw(config.ownedLabel === "lgbtq" ? "footer.proudlyLgbtqOwned" : "footer.proudlyGayOwned")}
          </p>
          <p>
            <Link href="/terms" className="underline hover:text-primary-500 transition-colors">
              {tRaw("footer.terms")}
            </Link>
            {" · "}
            <Link href="/privacy" className="underline hover:text-primary-500 transition-colors">
              {tRaw("footer.privacy")}
            </Link>
          </p>
        </div>
      </motion.div>

      <StickyCta href={ctaHref} label={config.cta} />
    </div>
  );
}

/** Built-in men's doc pages: copy resolved from i18n (pages.docs). */
type BuiltinVariant = "ceaseDesist" | "jurySummons" | "obituary";

const BUILTIN_EVENTS: Record<BuiltinVariant, string> = {
  ceaseDesist: "doc_ceasedesist_landing",
  jurySummons: "doc_jurysummons_landing",
  obituary: "doc_obituary_landing",
};

const BUILTIN_DOCKETS: Record<BuiltinVariant, string> = {
  ceaseDesist: "CD",
  jurySummons: "JS",
  obituary: "OB",
};

function BuiltinDoc({ variant }: { variant: BuiltinVariant }) {
  const { t: tRaw } = useT("landing");
  const t = (key: string) => tRaw(`pages.docs.${key}`);
  const v = (key: string) => tRaw(`pages.docs.variants.${variant}.${key}`);

  const config: DocConfig = {
    posthogEvent: BUILTIN_EVENTS[variant],
    audienceParam: "m",
    letterhead: t("letterhead"),
    docket: `${t("docketPrefix")}-${BUILTIN_DOCKETS[variant]}`,
    stamp: v("stamp"),
    title: v("title"),
    re: v("re"),
    intros: [v("intro1"), v("intro2")],
    findingsTitle: v("findingsTitle"),
    exhibits: [v("exhibits.a"), v("exhibits.b"), v("exhibits.c")],
    remedyTitle: v("remedyTitle"),
    remedy: v("remedy"),
    signLine: t("signLine"),
    cta: t("cta"),
    ctaSub: t("ctaSub"),
    witnessTitle: t("witnessTitle"),
    witnesses: (["w1", "w2", "w3"] as const).map((k) => ({
      quote: t(`witnesses.${k}.quote`),
      author: t(`witnesses.${k}.author`),
    })),
    objectionsTitle: t("objectionsTitle"),
    objections: (["dating", "shy", "cost"] as const).map((k) => ({
      q: t(`objections.${k}.q`),
      a: t(`objections.${k}.a`),
    })),
    footerGag: t("footerGag"),
    ownedLabel: "gay",
  };

  return <DocLayout config={config} />;
}

export const DocCeaseDesist = () => <BuiltinDoc variant="ceaseDesist" />;
export const DocJurySummons = () => <BuiltinDoc variant="jurySummons" />;
export const DocObituary = () => <BuiltinDoc variant="obituary" />;
export default DocLayout;
